import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
} from "@mui/material";
import { motion } from "framer-motion";
import SwanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";
import SwanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";

const capabilities = [
    {
        title: "Federated Queries",
        description:
            "Query across 40+ databases instantly. AIOS routes requests to the right data sources and merges results in real time — no manual joins or data exports.",
    },
    {
        title: "Agent Coordination",
        description:
            "Autonomous AI agents handle tasks across systems — scheduling, reporting, compliance checks, and data pipelines — without human intervention.",
    },
    {
        title: "Schema Discovery",
        description:
            "AIOS understands every table, field, and relationship across all connected databases. Ask a question in plain English and get the right data.",
    },
    {
        title: "Knowledge Graph",
        description:
            "A living map of every entity, relationship, and business rule across all Swan platforms. Context that grows smarter with every interaction.",
    },
    {
        title: "Decision Tracking & Audit",
        description:
            "Every agent action is logged, explainable, and auditable. Full transparency for compliance-sensitive K12 environments.",
    },
    {
        title: "Continuous Learning",
        description:
            "AIOS improves autonomously. Feedback from every query, decision, and outcome feeds back into better future performance.",
    },
];

const connectedSystems = [
    "FIG Finance",
    "Hi5 Nutrition",
    "POS Systems",
    "Bid-FIG",
    "Invoice Audit",
    "CRM",
    "Swan AI Labs",
    "LMS",
];

const architecturePillars = [
    {
        title: "Agent Registry",
        description:
            "Catalog of all registered AI agents — their capabilities, permissions, and real-time status across every connected system.",
    },
    {
        title: "Memory Layer",
        description:
            "Persistent context across conversations and tasks. Agents remember prior work, user preferences, and project history.",
    },
    {
        title: "Decision Engine",
        description:
            "Rules and ML models for autonomous decision-making. Every choice is logged with reasoning for full auditability.",
    },
    {
        title: "Learning Loop",
        description:
            "Feedback integration for continuous improvement. Corrections, outcomes, and patterns feed back into smarter future decisions.",
    },
];

export default function Aios() {
    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background:
                        "linear-gradient(120deg, #0b1a30 0%, #003366 40%, #00CFFE 100%)",
                    minHeight: { xs: "30vh", md: "38vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={SwanAiLabsLogo}
                    alt="AIOS"
                    sx={{
                        height: { xs: 80, md: 120 },
                        mb: 2,
                        filter: "drop-shadow(0 4px 32px rgba(0,207,254,0.25))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.2rem", md: "3.4rem" },
                        letterSpacing: "-0.04em",
                        mb: 1,
                        textShadow: "0 3px 16px rgba(25,60,110,0.2)",
                    }}
                >
                    AIOS
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                        mb: 2,
                        color: "rgba(255,255,255,0.95)",
                    }}
                >
                    Agentic Operating System
                </Typography>
                <Typography
                    sx={{
                        maxWidth: 800,
                        mx: "auto",
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 400,
                        fontSize: { xs: "1.05rem", md: "1.2rem" },
                        textShadow: "0 2px 14px rgba(30,140,160,0.13)",
                    }}
                >
                    The AI brain behind Swan Solutions. A centralized intelligence layer managing
                    40+ databases, coordinating autonomous agents, and powering every platform we build.
                </Typography>
            </Box>

            {/* What is AIOS */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                            textAlign: "center",
                        }}
                    >
                        One Platform. Every System. Total Intelligence.
                    </Typography>
                    <Typography
                        sx={{
                            color: "text.secondary",
                            fontSize: "1.15rem",
                            textAlign: "center",
                            maxWidth: 800,
                            mx: "auto",
                            mb: 6,
                        }}
                    >
                        AIOS is the centralized AI backbone that connects, coordinates, and makes
                        every Swan Solutions platform smarter. It federates data across 40+ databases,
                        orchestrates autonomous agents, and provides a unified intelligence layer for
                        K12 operations nationwide.
                    </Typography>
                </motion.div>

                {/* Hub and Spoke */}
                <Grid container spacing={3} justifyContent="center" alignItems="center">
                    {/* Surrounding systems - top row */}
                    {connectedSystems.slice(0, 4).map((system, i) => (
                        <Grid item xs={6} sm={3} key={system}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.08 * i, duration: 0.4 }}
                            >
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        textAlign: "center",
                                        borderRadius: 3,
                                        bgcolor: "#fff",
                                        border: "2px solid #e0f2f7",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: "#145c8c",
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        {system}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}

                    {/* Center AIOS hub */}
                    <Grid item xs={12}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.85 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                        >
                            <Paper
                                elevation={6}
                                sx={{
                                    p: { xs: 3, md: 4 },
                                    textAlign: "center",
                                    borderRadius: 5,
                                    maxWidth: 420,
                                    mx: "auto",
                                    background:
                                        "linear-gradient(135deg, #003366 0%, #00CFFE 100%)",
                                    color: "#fff",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={SwanAiLabsLogo}
                                    alt="AIOS"
                                    sx={{
                                        height: 56,
                                        mb: 1,
                                        filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
                                    }}
                                />
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 900, mb: 0.5 }}
                                >
                                    AIOS
                                </Typography>
                                <Typography sx={{ fontSize: "0.95rem", opacity: 0.9 }}>
                                    40+ Databases &bull; Autonomous Agents &bull; Federated Intelligence
                                </Typography>
                            </Paper>
                        </motion.div>
                    </Grid>

                    {/* Surrounding systems - bottom row */}
                    {connectedSystems.slice(4).map((system, i) => (
                        <Grid item xs={6} sm={3} key={system}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.08 * (i + 4), duration: 0.4 }}
                            >
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        textAlign: "center",
                                        borderRadius: 3,
                                        bgcolor: "#fff",
                                        border: "2px solid #e0f2f7",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: "#145c8c",
                                            fontSize: "0.95rem",
                                        }}
                                    >
                                        {system}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Key Capabilities */}
            <Box sx={{ bgcolor: "#eaf5fa", py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: "primary.main",
                                mb: 5,
                                textAlign: "center",
                            }}
                        >
                            Key Capabilities
                        </Typography>
                    </motion.div>
                    <Grid container spacing={4}>
                        {capabilities.map((cap, i) => (
                            <Grid item xs={12} sm={6} md={4} key={cap.title}>
                                <motion.div
                                    initial={{ opacity: 0, y: 28 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.07 * i, duration: 0.5 }}
                                    whileHover={{ scale: 1.025 }}
                                    style={{ height: "100%" }}
                                >
                                    <Card
                                        sx={{
                                            height: "100%",
                                            borderRadius: 4,
                                            boxShadow: "0 3px 24px 0 rgba(20,76,100,0.07)",
                                            p: 3,
                                        }}
                                    >
                                        <CardContent>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1.5,
                                                    color: "#145c8c",
                                                    fontSize: "1.12rem",
                                                }}
                                            >
                                                {cap.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "1.02rem",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {cap.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Architecture Overview */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                            textAlign: "center",
                        }}
                    >
                        Architecture
                    </Typography>
                    <Typography
                        sx={{
                            color: "text.secondary",
                            fontSize: "1.12rem",
                            textAlign: "center",
                            maxWidth: 700,
                            mx: "auto",
                            mb: 5,
                        }}
                    >
                        Four pillars power the AIOS intelligence layer.
                    </Typography>
                </motion.div>
                <Grid container spacing={4}>
                    {architecturePillars.map((pillar, i) => (
                        <Grid item xs={12} sm={6} key={pillar.title}>
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 * i, duration: 0.5 }}
                                style={{ height: "100%" }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: "100%",
                                        p: { xs: 3, md: 4 },
                                        borderRadius: 4,
                                        bgcolor: "#eaf5fa",
                                        border: "1px solid #d0e8f0",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            mb: 1.5,
                                            color: "primary.main",
                                        }}
                                    >
                                        {pillar.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "1.02rem",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {pillar.description}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* CTA */}
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Box
                    sx={{
                        background: "linear-gradient(135deg, #003366 0%, #00CFFE 100%)",
                        borderRadius: 5,
                        boxShadow: 6,
                        p: { xs: 4, md: 6 },
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={SwanSolutionsLogo}
                        alt="Swan Solutions"
                        sx={{
                            height: { xs: 48, md: 64 },
                            mb: 2,
                            filter: "brightness(0) invert(1) drop-shadow(0 4px 16px rgba(0,0,0,0.15))",
                        }}
                    />
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 2, color: "#fff" }}
                    >
                        See AIOS in Action
                    </Typography>
                    <Typography
                        sx={{
                            mb: 3,
                            color: "rgba(255,255,255,0.9)",
                            fontSize: "1.1rem",
                            maxWidth: 600,
                            mx: "auto",
                        }}
                    >
                        Discover how AIOS connects every Swan Solutions platform into a unified,
                        intelligent ecosystem for your district.
                    </Typography>
                    <Button
                        href="/contact"
                        variant="contained"
                        size="large"
                        component={motion.a}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        sx={{
                            borderRadius: 99,
                            fontWeight: 700,
                            px: 5,
                            py: 1.4,
                            bgcolor: "#fff",
                            color: "#003366",
                            fontSize: "1.12rem",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,0.9)",
                            },
                        }}
                    >
                        Schedule a Demo
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}
